import React, { useEffect, useMemo, useState } from "react"
import { BusinessHours, WeekDayEnum, WeekDayLabel, WorkingHour } from "./type"
import Select from "react-select"
import { SelectOption } from "../../types/SelectOption"

interface Props
{
    workingHours?: WorkingHour
    onChange?: (updatedWorkingHour: WorkingHour) => void
    isEditing: boolean
    isSpecificPeriod: boolean
    allWorkingHours?: WorkingHour[]
    dealerships?: SelectOption[]
    hideDealershipSelect?: boolean
}

ModalWorkingHoursForm.defaultProps = {
    isSpecificPeriod: false,
}

export default function ModalWorkingHoursForm(props: Props) {
    const initTimeSlots: BusinessHours = {
        [WeekDayEnum.Monday]: [ [ '08:00', '12:00' ], [ '14:00', '18:00' ] ],
        [WeekDayEnum.Tuesday]: [ [ '08:00', '12:00' ], [ '14:00', '18:00' ] ],
        [WeekDayEnum.Wednesday]: [ [ '08:00', '12:00' ], [ '14:00', '18:00' ] ],
        [WeekDayEnum.Thursday]: [ [ '08:00', '12:00' ], [ '14:00', '18:00' ] ],
        [WeekDayEnum.Friday]: [ [ '08:00', '12:00' ], [ '14:00', '18:00' ] ],
    }

    const initialSelectedDays = useMemo(() => {
        if (props.workingHours?.schedules.businessHours && Object.keys(props.workingHours.schedules.businessHours).length > 0) {
            return Object.keys(props.workingHours.schedules.businessHours).map(key => key as WeekDayEnum)
        }
        return [ WeekDayEnum.Monday, WeekDayEnum.Tuesday, WeekDayEnum.Wednesday, WeekDayEnum.Thursday, WeekDayEnum.Friday ]
    }, [ props.workingHours ])

    const initialTimeSlots = useMemo(() => {
        if (props.workingHours?.schedules.businessHours && Object.keys(props.workingHours.schedules.businessHours).length > 0) {
            return props.workingHours.schedules.businessHours
        }
        return initTimeSlots
    }, [ props.workingHours ])

    const [ selectedDays, setSelectedDays ] = useState<WeekDayEnum[]>(initialSelectedDays)
    const [ timeSlots, setTimeSlots ] = useState<BusinessHours>(initialTimeSlots)

    useEffect(() => {
        setSelectedDays(initialSelectedDays)
        setTimeSlots(initialTimeSlots)
    }, [ initialSelectedDays, initialTimeSlots ])

    useEffect(() => {
        if (props.onChange && props.workingHours) {
            props.onChange({
                ...props.workingHours,
                schedules: {
                    ...props.workingHours.schedules,
                    businessHours: timeSlots,
                },
            })
        }
    }, [ timeSlots ])

    const weekDays = [
        WeekDayEnum.Monday,
        WeekDayEnum.Tuesday,
        WeekDayEnum.Wednesday,
        WeekDayEnum.Thursday,
        WeekDayEnum.Friday,
        WeekDayEnum.Saturday,
        WeekDayEnum.Sunday,
    ]

    const sortedSelectedDays = useMemo(() => {
        return [ ...selectedDays ].sort((a, b) => parseInt(a) - parseInt(b))
    }, [ selectedDays ])

    const getAvailableDealerships = useMemo(() => {
        if (!props.workingHours || !props.allWorkingHours || !props.dealerships) {
            return props.dealerships || []
        }

        const currentDepartment = props.workingHours.department
        const currentWorkingHourId = props.workingHours.id

        // Récupérer tous les sites déjà utilisés pour ce département (sauf la config actuelle en mode édition)
        const usedSiteIds = props.allWorkingHours
            .filter(wh =>
                wh.department === currentDepartment &&
                wh.id !== currentWorkingHourId &&
                wh.dealershipIds &&
                wh.dealershipIds.length > 0
            )
            .flatMap(wh => wh.dealershipIds || [])

        // Filtrer les sites déjà utilisés
        return props.dealerships.filter(d => !usedSiteIds.includes(d.value as number))
    }, [props.dealerships, props.workingHours, props.allWorkingHours])

    const getShortLabel = (day: WeekDayEnum): string => {
        return WeekDayLabel[day].substring(0, 2)
    }

    const toggleDay = (day: WeekDayEnum): void => {
        if (selectedDays.includes(day)) {
            setSelectedDays(selectedDays.filter(d => d !== day))
            const newTimeSlots = { ...timeSlots }
            delete newTimeSlots[day]
            setTimeSlots(newTimeSlots)
        } else {
            setSelectedDays([ ...selectedDays, day ])
            setTimeSlots({ ...timeSlots, [day]: [ [ "", "" ], [ "", "" ] ] })
        }
    }

    const isSelected = (day: WeekDayEnum): boolean => {
        return selectedDays.includes(day)
    }

    const addTimeSlot = (day: WeekDayEnum): void => {
        const currentSlots = timeSlots[day] || []
        setTimeSlots({ ...timeSlots, [day]: [ ...currentSlots, [ "", "" ] ] })
    }

    const removeTimeSlot = (day: WeekDayEnum, index: number): void => {
        const currentSlots = timeSlots[day] || []
        const newSlots = currentSlots.filter((timeSlot, i) => i !== index)
        setTimeSlots({ ...timeSlots, [day]: newSlots })
    }

    const updateTimeSlot = (day: WeekDayEnum, index: number, timeIndex: 0 | 1, value: string): void => {
        const currentSlots = timeSlots[day] || []
        const newSlots = [ ...currentSlots ]
        newSlots[index][timeIndex] = value
        setTimeSlots({ ...timeSlots, [day]: newSlots })
    }

    const onDealershipIdsChange = (options: SelectOption[] | null): void => {
        if (!props.workingHours || !props.onChange) return

        const dealershipIds = options && options.length > 0
            ? options.map(opt => opt.value as number)
            : null

        props.onChange({
            ...props.workingHours,
            dealershipIds,
        })
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {(!props.isEditing && !props.isSpecificPeriod && !props.hideDealershipSelect) &&
              <div className="tw-flex tw-flex-row tw-items-center tw-text-sm tw-gap-5">
                <span className="tw-whitespace-nowrap tw-w-20">Site(s) :</span>
                <Select
                  multi={true}
                  style={{ minWidth: '20em', maxHeight: '30em' }}
                  value={getAvailableDealerships.filter(d => props.workingHours?.dealershipIds?.includes(d.value as number))}
                  onChange={onDealershipIdsChange}
                  options={getAvailableDealerships}
                  placeholder="Tous"
                />
              </div>}
            <div className="tw-flex tw-flex-row tw-gap-2">
                {weekDays.map((day) => (
                    <div
                        key={day}
                        onClick={() => toggleDay(day)}
                        className={`tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-medium tw-cursor-pointer tw-transition-colors ${
                            isSelected(day)
                                ? 'tw-bg-blue-500 tw-text-white hover:tw-bg-blue-600'
                                : 'tw-bg-gray-200 tw-text-gray-600 hover:tw-bg-gray-300'
                        }`}
                    >
                        {getShortLabel(day)}
                    </div>
                ))}
            </div>

            {selectedDays.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-2">
                    <div className="tw-w-full tw-flex tw-flex-col">
                        {sortedSelectedDays.map((day) => (
                            <div key={day} className="tw-flex tw-flex-row tw-items-center border-bottom tw-py-1 tw-gap-2">
                                <span className="tw-font-medium tw-text-sm tw-w-1/12">
                                    {WeekDayLabel[day]}
                                </span>
                                <div className="tw-flex tw-flex-row tw-items-center tw-gap-4 tw-flex-1 tw-flex-wrap">
                                    {(timeSlots[day] || []).map((slot, index) => (
                                        <div key={index} className="tw-flex tw-flex-row tw-p-2">
                                            <div className="tw-flex tw-gap-1 tw-items-center">
                                                <input
                                                    type="time"
                                                    value={slot[0]}
                                                    onChange={(e) => updateTimeSlot(day, index, 0, e.target.value)}
                                                    className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm"
                                                />
                                                <span>-</span>
                                                <input
                                                    type="time"
                                                    value={slot[1]}
                                                    onChange={(e) => updateTimeSlot(day, index, 1, e.target.value)}
                                                    className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm"
                                                />
                                            </div>
                                            {((timeSlots[day]?.length || 0) > 1 && index === (timeSlots[day]?.length ?? 0) - 1) && (
                                                <button
                                                    onClick={() => removeTimeSlot(day, index)}
                                                    className="tw-bg-transparent tw-border-0 tw-text-red-500 tw-text-lg"
                                                    title="Supprimer ce créneau"
                                                >
                                                    <i className="fa-solid fa-circle-xmark"></i>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => addTimeSlot(day)}
                                        className="tw-text-white tw-text-xs tw-bg-blue-500 tw-rounded tw-border-0 tw-px-2 tw-py-1"
                                    >
                                        <i className="fa fa-plus"></i>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
